import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuthStore } from '../auth';
import { useProject } from '../projects/ProjectContext';
import type { Project } from '../projects/types';
import type { SceneMode } from './components/ProjectTerritorialScene';
import { ProjectTerritorialScene } from './components/ProjectTerritorialScene';
import { ShellHeader } from './components/ShellHeader';
import { TerritorialDock } from './components/TerritorialDock';
import './project-dashboard.css';

// ─── Territorial backdrop ─────────────────────────────────────────────────────

function TerritorialBackdrop(): JSX.Element {
  return (
    <div className="ts-backdrop" aria-hidden="true">
      <svg
        className="ts-backdrop-svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g className="ts-net-edges">
          <line x1="180" y1="80" x2="340" y2="260" />
          <line x1="180" y1="80" x2="620" y2="65" />
          <line x1="620" y1="65" x2="700" y2="230" />
          <line x1="620" y1="65" x2="1100" y2="90" />
          <line x1="1100" y1="90" x2="1300" y2="265" />
          <line x1="1380" y1="75" x2="1300" y2="265" />
          <line x1="340" y1="260" x2="480" y2="420" />
          <line x1="340" y1="260" x2="700" y2="230" />
          <line x1="700" y1="230" x2="720" y2="450" />
          <line x1="700" y1="230" x2="1020" y2="250" />
          <line x1="1020" y1="250" x2="960" y2="430" />
          <line x1="1300" y1="265" x2="960" y2="430" />
          <line x1="480" y1="420" x2="380" y2="650" />
          <line x1="480" y1="420" x2="720" y2="450" />
          <line x1="720" y1="450" x2="660" y2="620" />
          <line x1="720" y1="450" x2="960" y2="430" />
          <line x1="960" y1="430" x2="900" y2="645" />
          <line x1="960" y1="430" x2="1160" y2="635" />
          <line x1="380" y1="650" x2="580" y2="820" />
          <line x1="660" y1="620" x2="580" y2="820" />
          <line x1="660" y1="620" x2="900" y2="645" />
          <line x1="900" y1="645" x2="860" y2="800" />
          <line x1="900" y1="645" x2="1160" y2="635" />
          <line x1="1160" y1="635" x2="1140" y2="815" />
          <line x1="80" y1="240" x2="340" y2="260" />
          <line x1="80" y1="240" x2="200" y2="440" />
          <line x1="200" y1="440" x2="380" y2="650" />
          <line x1="1220" y1="445" x2="1300" y2="265" />
          <line x1="1220" y1="445" x2="1160" y2="635" />
        </g>
        <g className="ts-net-nodes">
          <circle cx="180" cy="80" r="3" />
          <circle cx="620" cy="65" r="4" className="ts-net-node--key" />
          <circle cx="1100" cy="90" r="3" />
          <circle cx="1380" cy="75" r="3" />
          <circle cx="80" cy="240" r="3" />
          <circle cx="340" cy="260" r="4" className="ts-net-node--key" />
          <circle cx="700" cy="230" r="5" className="ts-net-node--hub" />
          <circle cx="1020" cy="250" r="3" />
          <circle cx="1300" cy="265" r="3" />
          <circle cx="200" cy="440" r="3" />
          <circle cx="480" cy="420" r="4" className="ts-net-node--key" />
          <circle cx="720" cy="450" r="5" className="ts-net-node--hub" />
          <circle cx="960" cy="430" r="4" className="ts-net-node--key" />
          <circle cx="1220" cy="445" r="3" />
          <circle cx="380" cy="650" r="3" />
          <circle cx="660" cy="620" r="4" className="ts-net-node--key" />
          <circle cx="900" cy="645" r="5" className="ts-net-node--hub" />
          <circle cx="1160" cy="635" r="3" />
          <circle cx="580" cy="820" r="3" />
          <circle cx="860" cy="800" r="3" />
          <circle cx="1140" cy="815" r="4" className="ts-net-node--key" />
        </g>
      </svg>
    </div>
  );
}

// ─── Center identity ──────────────────────────────────────────────────────────

function TerritorialIdentity(): JSX.Element {
  return (
    <div className="ts-identity">
      <p className="ts-identity-sub">Centro territorial</p>
      <h1 className="ts-identity-word">WAYLINE</h1>
      <p className="ts-identity-caption">La red es Wayline.</p>
    </div>
  );
}

// ─── No-project notice ────────────────────────────────────────────────────────

interface NoProjectNoticeProps {
  isLoading: boolean;
  hasProjects: boolean;
}

function NoProjectNotice({ isLoading, hasProjects }: NoProjectNoticeProps): JSX.Element | null {
  if (isLoading || hasProjects) return null;
  return <p className="ts-no-project">Sin proyecto activo · Contactá al administrador</p>;
}

// ─── Territorial scene mount guard ───────────────────────────────────────────
// Retrasa el montaje de ProjectTerritorialScene un tick después de que el
// proyecto y sus bounds estén confirmados como estables. Evita que el mapa
// arranque con estado parcial durante el bootstrap de auth/proyectos.
// key={project.id} garantiza remount limpio si cambia el proyecto activo.

interface TerritorialSceneGuardProps {
  project: Project;
  isLoading: boolean;
  sceneError: boolean;
  onSceneError: () => void;
  sceneMode: SceneMode;
}

function TerritorialSceneGuard({
  project,
  isLoading,
  sceneError,
  onSceneError,
  sceneMode,
}: TerritorialSceneGuardProps): JSX.Element {
  const [isBootstrapped, setIsBootstrapped] = useState(false);

  useEffect(() => {
    if (!isLoading && !sceneError) {
      setIsBootstrapped(true);
    }
    // Sin cleanup: una vez bootstrapped, el mapa permanece montado aunque
    // isLoading cambie por ciclos secundarios de fetchProjects (Strict Mode,
    // auth re-check). El reset ocurre naturalmente cuando el componente
    // desmonta por key={project.id} al cambiar de proyecto.
  }, [isLoading, sceneError]);

  if (!isBootstrapped) return <TerritorialBackdrop />;

  return (
    <ProjectTerritorialScene
      key={project.id}
      projectId={project.id}
      bounds={project.bounds}
      sceneMode={sceneMode}
      onSceneError={onSceneError}
    />
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

function readStoredSceneMode(): SceneMode {
  const stored = localStorage.getItem('ts-scene-mode');
  if (stored === 'light') return 'light';
  if (stored === 'topographic') return 'topographic';
  if (stored === 'satellite') return 'satellite';
  return 'dark';
}

/** ProjectDashboard — Territorial Entry Shell. La red es Wayline. */
export function ProjectDashboard(): JSX.Element {
  const { user } = useAuthStore();
  const { projectId, availableProjects, isLoading, hasProjects } = useProject();
  const [sceneError, setSceneError] = useState(false);
  const [sceneMode, setSceneMode] = useState<SceneMode>(readStoredSceneMode);

  const isSuperAdmin = user?.role?.name === 'superadmin';

  // Memoizado para evitar nuevas referencias por objeto en cada render de contexto.
  const activeProject = useMemo(
    () => (projectId ? (availableProjects.find(p => p.id === projectId) ?? null) : null),
    [projectId, availableProjects]
  );

  const projectModules = activeProject?.activeModules;
  const userName = user ? `${user.firstName} ${user.lastName}` : '';
  const onSceneError = useCallback((): void => setSceneError(true), []);
  const onSceneModeChange = useCallback((mode: SceneMode): void => {
    setSceneMode(mode);
    localStorage.setItem('ts-scene-mode', mode);
  }, []);
  const showIdentity = !activeProject || isLoading || sceneError;

  return (
    <div className="ts-layout">
      {activeProject && !sceneError ? (
        <TerritorialSceneGuard
          project={activeProject}
          isLoading={isLoading}
          sceneError={sceneError}
          onSceneError={onSceneError}
          sceneMode={sceneMode}
        />
      ) : (
        <TerritorialBackdrop />
      )}
      <ShellHeader
        userName={userName}
        projectName={activeProject?.name ?? null}
        sceneMode={sceneMode}
        onSceneModeChange={onSceneModeChange}
      />
      <div className="ts-center">
        {showIdentity && <TerritorialIdentity />}
        <NoProjectNotice isLoading={isLoading} hasProjects={hasProjects} />
      </div>
      <TerritorialDock
        hasProjects={hasProjects}
        projectModules={projectModules}
        isSuperAdmin={isSuperAdmin}
      />
    </div>
  );
}
